/* eslint-disable no-bitwise */
import { EIGHT_SHORT_SEQUENCE, LONG_START_SEQUENCE, LONG_STOP_SEQUENCE, ONLY_LONG_SEQUENCE } from './data';
import type { MDCT } from './mdct';
import { applyTemporalNoiseShaping } from './temporalNoiseShaping';
import { CouplingPoint, type SingleChannelElement, type ChannelPairElement, type LfeChannelElement, type CouplingChannelElement, type ChannelElement, isSCE, isCPE, isCCE } from './types';
import { get_kaiser_bessel_1024, get_kaiser_bessel_128, get_sine_1024, get_sine_128 } from './windowTables';


/**
 * The first step in the decoding process is to get for each channel element
 * an array of 1024 spectral values. This function will process these values
 * to convert them into actual audio samples.
 *
 * Returns a 2 dimensional array so that t[ch][s] is an integer representing the
 * PCM S16LE sample #s for the channel #ch or a float between -1.0 and 1.0 if
 * emitPCMs16LESamples is false. The channels are sorted by their channelIndex
 * property.
 */
export function convertSpectralValuesToSamples(
  channelElements: (SingleChannelElement | ChannelPairElement | LfeChannelElement | CouplingChannelElement)[],
  mdctLong: MDCT,
  mdctShort: MDCT,
  tns_tmp: Float32Array,
  tns_lpc: Float32Array,
  imdct_and_window_buf: Float32Array,
  imdct_and_window_tmp: Float32Array,
) {
  for (const che of channelElements) {
    if (isSCE(che) || isCPE(che)) {
      apply_channel_coupling(che, channelElements, CouplingPoint.BEFORE_TNS, true);
    }

    if (che.channel0.tns !== undefined) {
      applyTemporalNoiseShaping(che.channel0.coeffs, che.channel0.tns, che.channel0.ics_info, che.channel0.scalefactorBandsAndGroupingInfo, true, tns_tmp, tns_lpc);
    }

    if (isCPE(che) && che.channel1.tns !== undefined) {
      applyTemporalNoiseShaping(che.channel1.coeffs, che.channel1.tns, che.channel1.ics_info, che.channel1.scalefactorBandsAndGroupingInfo, true, tns_tmp, tns_lpc);
    }

    if (isSCE(che) || isCPE(che)) {
      apply_channel_coupling(che, channelElements, CouplingPoint.BETWEEN_TNS_AND_IMDCT, true);
    }

    if (!isCCE(che) || che.couplingPoint == CouplingPoint.AFTER_IMDCT) {
      imdct_and_window(che.channel0, mdctLong, mdctShort, imdct_and_window_buf, imdct_and_window_tmp);

      if (isCPE(che)) {
        imdct_and_window(che.channel1, mdctLong, mdctShort, imdct_and_window_buf, imdct_and_window_tmp);
      }
    }

    if (isSCE(che) || isCPE(che)) {
      apply_channel_coupling(che, channelElements, CouplingPoint.AFTER_IMDCT, false);
    }
  }
}


/**
 * Converts a float value between -1 and 1 to a signed integer between -32768 and 32767
 */
export function convertFloatPcmToS16LE(value: number): number {
  let n = Math.floor(value * 32768);
  if (n > 32767) n = 32767;
  else if (n < -32768) n = -32768;

  return n & 0xFFFF;
}




function imdct_and_window(
  ch: ChannelElement,
  mdctLong: MDCT,
  mdctShort: MDCT,
  buf: Float32Array,
  temp: Float32Array,
) {
  const shortWindow = ch.ics_info.current_window_shape === 1 ? get_kaiser_bessel_128() : get_sine_128();
  const previousShortWindow = ch.ics_info.previous_window_shape === 1 ? get_kaiser_bessel_128() : get_sine_128();
  const previousLongWindow = ch.ics_info.previous_window_shape === 1 ? get_kaiser_bessel_1024() : get_sine_1024();
  const input = ch.coeffs;
  const output = ch.samples;
  const overlap = ch.overlaps;

  if (ch.ics_info.current_window_sequence === EIGHT_SHORT_SEQUENCE) {
    for (let i = 0; i < 1024; i += 128) {
      mdctShort.process(input, i, buf, i);
    }
  } else {
    mdctLong.process(input, 0, buf, 0);
  }

  // Calculate the output using the output of the MDCT and the overlap values saved from the last frame
  if ((ch.ics_info.previous_window_sequence == ONLY_LONG_SEQUENCE || ch.ics_info.previous_window_sequence == LONG_STOP_SEQUENCE) &&
      (ch.ics_info.current_window_sequence === ONLY_LONG_SEQUENCE || ch.ics_info.current_window_sequence === LONG_START_SEQUENCE)) {
    apply_windowing(output, 0, overlap, 0, buf, 0, previousLongWindow);
  } else {
    for (let i = 0; i < 448; i++) {
      output[i] = overlap[i]!;
    }

    if (ch.ics_info.current_window_sequence === EIGHT_SHORT_SEQUENCE) {
      apply_windowing(output, 448          , overlap,          448, buf,       0, previousShortWindow);
      apply_windowing(output, 448 + 1 * 128, buf,               64, buf, 1 * 128, shortWindow);
      apply_windowing(output, 448 + 2 * 128, buf,     1 * 128 + 64, buf, 2 * 128, shortWindow);
      apply_windowing(output, 448 + 3 * 128, buf,     2 * 128 + 64, buf, 3 * 128, shortWindow);
      apply_windowing(temp,               0, buf,     3 * 128 + 64, buf, 4 * 128, shortWindow);
      for (let i = 0; i < 64; i++) {
        output[448 + 4 * 128 + i] = temp[i]!;
      }
    } else {
      apply_windowing(output, 448, overlap, 448, buf, 0, previousShortWindow);
      for (let i = 0; i < 448; i++) {
        output[576 + i] = buf[64 + i]!;
      }
    }
  }

  // Save overlap values for calculation on the next frame
  if (ch.ics_info.current_window_sequence === EIGHT_SHORT_SEQUENCE) {
    for (let i = 0; i < 64; i++) {
      overlap[i] = temp[64 + i]!;
    }
    apply_windowing(overlap,   64, buf,  4 * 128 + 64, buf, 5 * 128, shortWindow);
    apply_windowing(overlap,  192, buf,  5 * 128 + 64, buf, 6 * 128, shortWindow);
    apply_windowing(overlap,  320, buf,  6 * 128 + 64, buf, 7 * 128, shortWindow);
    for (let i = 0; i < 64; i++) {
      overlap[448 + i] = buf[7 * 128 + 64 + i]!;
    }
  } else if (ch.ics_info.current_window_sequence === LONG_START_SEQUENCE) {
    for (let i = 0; i < 448; i++) {
      overlap[i] = buf[512 + i]!;
    }
    for (let i = 0; i < 64; i++) {
      overlap[448 + i] = buf[7 * 128 + 64 + i]!;
    }
  } else { // LONG_STOP or ONLY_LONG
    for (let i = 0; i < 512; i++) {
      overlap[i] = buf[512 + i]!;
    }
  }
}


/**
 * This is a port of ffmpeg's vector_fmul_window_c
 */
function apply_windowing(
  dst: Float32Array,
  dst_offset: number,
  src0: Float32Array,
  src0_offset: number,
  src1: Float32Array,
  src1_offset: number,
  win: Float32Array,
) {
  const len = win.length / 2;

  for (let index = 0; index < len; index++) {
    dst[dst_offset + index] = src0[src0_offset + index]! * win[2 * len - 1 - index]! - src1[src1_offset + len - 1 - index]! * win[index]!;
  }

  for (let index = len; index < 2 * len; index++) {
    dst[dst_offset + index] = src0[src0_offset + 2 * len - 1 - index]! * win[2 * len - 1 - index]! + src1[src1_offset + index - len]! * win[index]!;
  }
}


function apply_channel_coupling(
  elment: SingleChannelElement | ChannelPairElement,
  channelElements: (SingleChannelElement | ChannelPairElement | LfeChannelElement | CouplingChannelElement)[],
  couplingPoint: CouplingPoint,
  applyDependentCoupling: boolean,
) {
  const cce = channelElements.find(c => isCCE(c) && c.couplingPoint === couplingPoint);
  if (cce === undefined) {
    return;
  }

  // See comment in coupling_channel_element()
  throw 'not implemented';
}
